"use client"

import { createContext, useRef, useState, useCallback, useMemo } from "react"
import type { ReactNode, RefObject } from "react"

export interface SubContentData {
  itemId: string
  subContent: ReactNode
  title?: string
}

export const SubContentContext = createContext<{
  subContentMap: Map<string, SubContentData>
  registerSubContent: (itemId: string, data: SubContentData) => void
  unregisterSubContent: (itemId: string) => void
  getSubContent: (itemId: string) => SubContentData | undefined
} | null>(null)
import type {
  SidebarContextValue,
  SidebarCollapseMode,
  SidebarHideBehaviour,
  SidebarHideOpensBehavior,
  SidebarLayoutBehaviour,
  SidebarDimensions,
  SidebarSafeAreas,
  SidebarAnimations,
  SidebarLiquidGlassConfig,
} from "./Sidebar.types"
import {
  DEFAULT_DIMENSIONS,
  DEFAULT_SAFE_AREAS,
  DEFAULT_ANIMATIONS,
  DEFAULT_VISUAL_STYLE,
  DEFAULT_CONFIG,
} from "./Sidebar.constants"
import { DEFAULT_LIQUID_GLASS_CONFIG } from "./sidebar-defaults"

/**
 * Contexto del Sidebar para compartir estado entre componentes primitivos
 * @constant
 */
export const SidebarContext = createContext<SidebarContextValue | null>(null)

/**
 * Props del SidebarProvider
 * @interface
 */
interface SidebarProviderProps {
  /** Contenido hijo (componentes primitivos del sidebar) */
  children: ReactNode
  /** Estado inicial abierto (modo uncontrolled) */
  defaultOpen?: boolean
  /** Estado abierto (modo controlled) */
  open?: boolean
  /** Callback cuando cambia el estado open */
  onOpenChange?: (open: boolean) => void
  /** Modo de colapso del sidebar */
  collapseMode?: SidebarCollapseMode
  /** Comportamiento en modo hide */
  hideBehaviour?: SidebarHideBehaviour
  /** Comportamiento al abrir desde hide */
  hideOpensBehavior?: SidebarHideOpensBehavior
  /** Comportamiento de layout del sidebar */
  layoutBehaviour?: SidebarLayoutBehaviour
  /** Configuración de dimensiones */
  dimensions?: Partial<SidebarDimensions>
  /** Configuración de safe areas */
  safeAreas?: Partial<SidebarSafeAreas>
  /** Configuración de animaciones */
  animations?: Partial<SidebarAnimations>
  /** ID del toggle input */
  toggleId?: string
  /** Modo debug para visualizar safe areas */
  debug?: boolean
  /** Habilitar FluidHoverIndicator (glassmorphism + spring physics) */
  enableFluidIndicator?: boolean
  /** Habilitar tooltip en hover (muestra label o subcategorías) */
  enableTooltip?: boolean
  /** Configuración experimental de Liquid Glass V2 */
  liquidGlass?: Partial<SidebarLiquidGlassConfig>
}

/**
 * Provider del contexto del Sidebar
 *
 * @description
 * Componente que provee el contexto compartido a todos los componentes
 * primitivos del sidebar. Gestiona el estado global (open/closed) y
 * referencias a elementos DOM necesarios para funcionalidades como
 * el indicador visual y navegación por teclado.
 *
 * Soporta tanto modo controlled (pasando prop `open`) como uncontrolled
 * (usando `defaultOpen`).
 *
 * @example
 * ```tsx
 * // Modo uncontrolled
 * <SidebarProvider defaultOpen={true}>
 *   <SidebarNav>...</SidebarNav>
 * </SidebarProvider>
 *
 * // Modo controlled
 * <SidebarProvider
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 * >
 *   <SidebarNav>...</SidebarNav>
 * </SidebarProvider>
 * ```
 */
export function SidebarProvider({
  children,
  defaultOpen = DEFAULT_CONFIG.defaultOpen,
  open: controlledOpen,
  onOpenChange,
  collapseMode = DEFAULT_CONFIG.collapseMode,
  hideBehaviour = DEFAULT_CONFIG.hideBehaviour,
  hideOpensBehavior = DEFAULT_CONFIG.hideOpensBehavior,
  layoutBehaviour = DEFAULT_CONFIG.layoutBehaviour,
  dimensions: dimensionsProp,
  safeAreas: safeAreasProp,
  animations: animationsProp,
  toggleId = DEFAULT_CONFIG.toggleId,
  debug = DEFAULT_CONFIG.debug,
  enableFluidIndicator = false,
  enableTooltip = true,
  liquidGlass: liquidGlassProp,
}: SidebarProviderProps) {
  // Estado para open/closed (controlled o uncontrolled)
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen

  const setOpen = useCallback((newOpen: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }, [controlledOpen, onOpenChange])

  // Referencias a elementos DOM
  const navRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)

  // Estado de items registrados para navegación por teclado
  const [items, setItems] = useState<HTMLElement[]>([])

  // Estado para mostrar indicador cuando se navega por teclado
  const [showIndicatorForKeyboard, setShowIndicatorForKeyboard] = useState(false)

  // Estado compartido para indicar si el cursor/focus está sobre el área de items
  const [isOverItems, setIsOverItems] = useState(false)

  // Estado para el elemento actualmente hovered/focused (para FluidIndicator)
  const [currentItemElement, setCurrentItemElement] = useState<HTMLElement | null>(null)

  // Estado para SubContent (map de itemId → subContent)
  const [subContentMap] = useState(() => new Map<string, SubContentData>())

  /**
   * Registrar subContent de un item
   */
  const registerSubContent = useCallback((itemId: string, data: SubContentData) => {
    subContentMap.set(itemId, data)
  }, [subContentMap])

  /**
   * Des-registrar subContent de un item
   */
  const unregisterSubContent = useCallback((itemId: string) => {
    subContentMap.delete(itemId)
  }, [subContentMap])

  /**
   * Obtener subContent de un item
   */
  const getSubContent = useCallback((itemId: string) => {
    return subContentMap.get(itemId)
  }, [subContentMap])

  /**
   * Registrar un item para navegación por teclado
   */
  const registerItem = useCallback((element: HTMLElement) => {
    setItems((prev) => {
      // Evitar duplicados
      if (prev.includes(element)) return prev
      return [...prev, element]
    })
  }, [])

  /**
   * Des-registrar un item cuando se desmonta
   */
  const unregisterItem = useCallback((element: HTMLElement) => {
    setItems((prev) => prev.filter((item) => item !== element))
  }, [])

  // Merge de configuraciones con defaults (memoizado para evitar recreaciones innecesarias)
  const dimensions: SidebarDimensions = useMemo(
    () => ({
      ...DEFAULT_DIMENSIONS,
      ...dimensionsProp,
    }),
    [dimensionsProp],
  )

  const safeAreas: SidebarSafeAreas = useMemo(
    () => ({
      ...DEFAULT_SAFE_AREAS,
      ...safeAreasProp,
    }),
    [safeAreasProp],
  )

  const animations: SidebarAnimations = useMemo(
    () => ({
      ...DEFAULT_ANIMATIONS,
      ...animationsProp,
    }),
    [animationsProp],
  )

  const liquidGlass: SidebarLiquidGlassConfig = useMemo(
    () => ({
      ...DEFAULT_LIQUID_GLASS_CONFIG,
      ...liquidGlassProp,
    }),
    [liquidGlassProp],
  )

  // Valor del contexto (memoizado para evitar re-renders innecesarios)
  const contextValue: SidebarContextValue = useMemo(
    () => ({
      defaultOpen,
      open,
      setOpen,
      collapseMode,
      hideBehaviour,
      hideOpensBehavior,
      layoutBehaviour,
      dimensions,
      safeAreas,
      animations,
      toggleId,
      debug,
      navRef: navRef as RefObject<HTMLElement>,
      contentRef,
      indicatorRef,
      registerItem,
      unregisterItem,
      items,
      showIndicatorForKeyboard,
      setShowIndicatorForKeyboard,
      isOverItems,
      setIsOverItems,
      enableFluidIndicator,
      enableTooltip,
      currentItemElement,
      setCurrentItemElement,
      liquidGlass,
    }),
    [
      defaultOpen,
      open,
      setOpen,
      collapseMode,
      hideBehaviour,
      hideOpensBehavior,
      layoutBehaviour,
      dimensions,
      safeAreas,
      animations,
      liquidGlass,
      toggleId,
      debug,
      navRef,
      contentRef,
      indicatorRef,
      registerItem,
      unregisterItem,
      items,
      showIndicatorForKeyboard,
      setShowIndicatorForKeyboard,
      isOverItems,
      setIsOverItems,
      enableFluidIndicator,
      enableTooltip,
      currentItemElement,
      setCurrentItemElement,
    ],
  )

  // Valor del SubContentContext
  const subContentContextValue = useMemo(
    () => ({
      subContentMap,
      registerSubContent,
      unregisterSubContent,
      getSubContent,
    }),
    [subContentMap, registerSubContent, unregisterSubContent, getSubContent],
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <SubContentContext.Provider value={subContentContextValue}>
        {children}
      </SubContentContext.Provider>
    </SidebarContext.Provider>
  )
}
